import { appConfig } from "@/config";
import { baseRefreshingQuery } from "@/core/api/baseQuery";
import { api } from "@/core/api";
import { apiCacheTags } from "@/core/api/tags";
import {
  captureApi,
  CaptureApiTagType,
} from "@/modules/capture/services/captureApi";
import { dashboardConfigApi, DashboardConfigApiTagType } from "@/modules/dashboard/services/dashboardConfigApi";
import { modelApi, ModelApiTagType } from "@/modules/model/services/modelApi";
import { HttpMethod } from "@/types";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ArchivedLabel,
  CreateLabel,
  CreateProjectRequest,
  Label,
  LabelUsage,
  PreAnnotateSettingsDocument,
  Project,
  ProjectListResponse,
  SuggestLabelDescriptionRequest,
  SuggestLabelDescriptionResponse,
  UpdateLabel,
  UpdatePreAnnotateSettingsDocument,
  UpdateProjectRequest,
  VisionProjectNote,
  VisionProjectNotesResponse,
} from "@repo/schema";
import z from "zod";
import { setActiveProject } from "./projectActions";

export enum ProjectApiTagType {
  Projects = "Projects",
  ProjectLabels = "ProjectLabels",
  PreAnnotateSettings = "PreAnnotateSettings",
  VisionNotes = "VisionNotes",
}

const {
  projects,
  preAnnotateSettings: preAnnotateSettingsEndpoint,
  visionNotes,
  labelDescriptionSuggestion,
} = appConfig.studioApi.endpoints;

/**
 * Archiving or restoring a label changes data cached by other slices: task
 * status and counts, a model's badged label list, test-case limits, the
 * dataset analytics and the confidence report's regions.
 */
const invalidateLabelDependents = (dispatch: (action: any) => unknown) => {
  dispatch(captureApi.util.invalidateTags([CaptureApiTagType.Tasks]));
  dispatch(modelApi.util.invalidateTags([ModelApiTagType.Model]));
  dispatch(
    dashboardConfigApi.util.invalidateTags([
      DashboardConfigApiTagType.DashboardConfigs,
    ]),
  );
  dispatch(
    api.util.invalidateTags([
      apiCacheTags.eval.limits,
      apiCacheTags.eval.testCases,
      apiCacheTags.analytics.datasetStats,
      apiCacheTags.confidenceReport.report,
    ]),
  );
};

const projectApiBase = createApi({
  reducerPath: "projectApi",
  baseQuery: baseRefreshingQuery,
  tagTypes: Object.values(ProjectApiTagType),
  endpoints: () => ({}),
});

export const projectApi = projectApiBase.injectEndpoints({
  endpoints: (builder) => ({
    createProject: builder.mutation<Project, CreateProjectRequest>({
      query: (payload) => {
        return {
          url: projects.projects,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: (_result, _error) => [
        { type: ProjectApiTagType.Projects },
      ],
    }),
    getProjects: builder.query<Project[], void>({
      query: () => ({
        url: projects.projects,
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error) => [{ type: ProjectApiTagType.Projects }],
      transformResponse: (response: ProjectListResponse) => response.projects,
      onQueryStarted: (_, { queryFulfilled, dispatch }) => {
        queryFulfilled.then(
          ({ data }) => {
            const activeProjectId = z.coerce
              .number()
              .safeParse(sessionStorage.getItem("activeProjectId"));
            const activeProject = data.find(
              (x) => x.id === activeProjectId.data,
            );
            dispatch(setActiveProject(activeProject ?? null));
          },
          () => {
            // Fetch failed — the component surfaces the error. Without this
            // handler the rejection escapes as an unhandled promise and lands
            // in Sentry as an uncaught 401.
          },
        );
      },
    }),
    getProject: builder.query<Project, { projectId: number }>({
      query: ({ projectId }) => ({
        url: projects.project(projectId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: ProjectApiTagType.Projects, id: projectId },
      ],
    }),
    // Uses the shared request type rather than restating the fields, so adding
    // one to the schema cannot silently leave this endpoint behind.
    updateProject: builder.mutation<
      Project,
      UpdateProjectRequest & { projectId: number }
    >({
      query: ({ projectId, ...body }) => ({
        url: projects.project(projectId),
        method: HttpMethod.PUT,
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: ProjectApiTagType.Projects },
        { type: ProjectApiTagType.Projects, id: projectId },
      ],
    }),
    deleteProject: builder.mutation<void, { projectId: number }>({
      query: ({ projectId }) => ({
        url: projects.project(projectId),
        method: HttpMethod.DELETE,
      }),
      invalidatesTags: (_result, _error) => [
        { type: ProjectApiTagType.Projects },
      ],
    }),
    // Label mutations also invalidate the auto-annotate document: its
    // `labelDescriptions` are read from the labels, so a label created, edited
    // or archived elsewhere must show up in the settings dialog on next open.
    createProjectLabel: builder.mutation<
      Label,
      CreateLabel & { projectId: number }
    >({
      query: ({ projectId, ...payload }) => ({
        url: projects.projectLabels(projectId),
        method: HttpMethod.POST,
        body: payload,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        {
          type: ProjectApiTagType.Projects,
          id: projectId,
          subType: ProjectApiTagType.ProjectLabels,
        },
        { type: ProjectApiTagType.PreAnnotateSettings, id: projectId },
      ],
    }),
    getProjectLabels: builder.query<Label[], { projectId: number }>({
      query: ({ projectId }) => ({
        url: projects.projectLabels(projectId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { projectId }) => [
        {
          type: ProjectApiTagType.Projects,
          id: projectId,
          subType: ProjectApiTagType.ProjectLabels,
        },
      ],
    }),
    /**
     * Where a label is still referenced. Shares the labels tag so every label
     * mutation refetches it; the editor also refetches on open because saving
     * annotations does not touch this tag.
     */
    getProjectLabelUsage: builder.query<
      LabelUsage,
      { projectId: number; labelId: number }
    >({
      query: ({ projectId, labelId }) => ({
        url: projects.projectLabelUsage(projectId, labelId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { projectId }) => [
        {
          type: ProjectApiTagType.Projects,
          id: projectId,
          subType: ProjectApiTagType.ProjectLabels,
        },
      ],
    }),
    /**
     * Archived labels with their archival date and how many regions each one
     * hides. Shares the labels tag, so archive/restore/create/rename refetch it.
     */
    getArchivedProjectLabels: builder.query<
      ArchivedLabel[],
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: projects.projectLabelsArchived(projectId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { projectId }) => [
        {
          type: ProjectApiTagType.Projects,
          id: projectId,
          subType: ProjectApiTagType.ProjectLabels,
        },
      ],
    }),
    archiveProjectLabel: builder.mutation<
      Label,
      { projectId: number; labelId: number }
    >({
      query: ({ projectId, labelId }) => ({
        url: projects.projectLabelArchive(projectId, labelId),
        method: HttpMethod.POST,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        {
          type: ProjectApiTagType.Projects,
          id: projectId,
          subType: ProjectApiTagType.ProjectLabels,
        },
        { type: ProjectApiTagType.PreAnnotateSettings, id: projectId },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          invalidateLabelDependents(dispatch);
        } catch {
          // mutation failed — nothing changed
        }
      },
    }),
    restoreProjectLabel: builder.mutation<
      Label,
      { projectId: number; labelId: number }
    >({
      query: ({ projectId, labelId }) => ({
        url: projects.projectLabelRestore(projectId, labelId),
        method: HttpMethod.POST,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        {
          type: ProjectApiTagType.Projects,
          id: projectId,
          subType: ProjectApiTagType.ProjectLabels,
        },
        { type: ProjectApiTagType.PreAnnotateSettings, id: projectId },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          invalidateLabelDependents(dispatch);
        } catch {
          // mutation failed — nothing changed
        }
      },
    }),
    updateProjectLabel: builder.mutation<
      Label,
      { projectId: number; labelId: number } & UpdateLabel
    >({
      query: ({ projectId, labelId, ...body }) => ({
        url: projects.projectLabel(projectId, labelId),
        method: HttpMethod.PUT,
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        {
          type: ProjectApiTagType.Projects,
          id: projectId,
          subType: ProjectApiTagType.ProjectLabels,
        },
        { type: ProjectApiTagType.PreAnnotateSettings, id: projectId },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(captureApi.util.invalidateTags([CaptureApiTagType.Tasks]));
        } catch {
          // mutation failed — nothing to invalidate
        }
      },
    }),
    getPreAnnotateSettings: builder.query<
      PreAnnotateSettingsDocument,
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: preAnnotateSettingsEndpoint(projectId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: ProjectApiTagType.PreAnnotateSettings, id: projectId },
      ],
    }),
    /**
     * Saves the config (engine, default mode) and the label descriptions in
     * one transaction. The descriptions are written to the
     * labels themselves, so the label list is stale afterwards and gets the
     * same tag object `getProjectLabels` provides.
     */
    updatePreAnnotateSettings: builder.mutation<
      PreAnnotateSettingsDocument,
      { projectId: number; body: UpdatePreAnnotateSettingsDocument }
    >({
      query: ({ projectId, body }) => ({
        url: preAnnotateSettingsEndpoint(projectId),
        method: HttpMethod.PUT,
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: ProjectApiTagType.PreAnnotateSettings, id: projectId },
        {
          type: ProjectApiTagType.Projects,
          id: projectId,
          subType: ProjectApiTagType.ProjectLabels,
        },
      ],
    }),
    /**
     * A description written by Robopipe Vision from an example of the label.
     * Returns text for a form field and saves nothing, so no tags move.
     */
    suggestLabelDescription: builder.mutation<
      SuggestLabelDescriptionResponse,
      { projectId: number; body: SuggestLabelDescriptionRequest }
    >({
      query: ({ projectId, body }) => ({
        url: labelDescriptionSuggestion(projectId),
        method: HttpMethod.POST,
        body,
      }),
    }),
    /**
     * The project's Robopipe Vision notes, oldest first. Admin-only on the
     * server, like the settings dialog that lists them. Every feedback turn
     * adds one, so the label page invalidates the tag when a turn lands.
     */
    getVisionNotes: builder.query<VisionProjectNotesResponse, { projectId: number }>({
      query: ({ projectId }) => ({
        url: visionNotes.list(projectId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: ProjectApiTagType.VisionNotes, id: projectId },
      ],
    }),
    /**
     * Add a manual note: immediately, not on the dialog's Save. Appended to the
     * list once the server answers, so it lands with its real id and author.
     */
    createVisionNote: builder.mutation<VisionProjectNote, { projectId: number; text: string }>({
      query: ({ projectId, text }) => ({
        url: visionNotes.list(projectId),
        method: HttpMethod.POST,
        body: { text },
      }),
      onQueryStarted: async ({ projectId }, { dispatch, queryFulfilled }) => {
        try {
          const { data: note } = await queryFulfilled;
          dispatch(
            projectApi.util.updateQueryData("getVisionNotes", { projectId }, (draft) => {
              draft.notes.push(note);
            }),
          );
        } catch {
          // Nothing was patched; the caller shows the error.
        }
      },
    }),
    /**
     * Edit one note's text: immediately, not on the dialog's Save. Optimistic,
     * then replaced by the server's copy (which carries the new updatedAt); a
     * failure puts the old text back. Members edit from the run panel without
     * the list cached, where the patches are no-ops.
     */
    updateVisionNote: builder.mutation<
      VisionProjectNote,
      { projectId: number; noteId: number; text: string }
    >({
      query: ({ projectId, noteId, text }) => ({
        url: visionNotes.one(projectId, noteId),
        method: HttpMethod.PATCH,
        body: { text },
      }),
      onQueryStarted: async ({ projectId, noteId, text }, { dispatch, queryFulfilled }) => {
        const replace = (next: Partial<VisionProjectNote>) =>
          dispatch(
            projectApi.util.updateQueryData("getVisionNotes", { projectId }, (draft) => {
              const note = draft.notes.find((n) => n.id === noteId);
              if (note) Object.assign(note, next);
            }),
          );
        const patch = replace({ text });
        try {
          const { data } = await queryFulfilled;
          replace(data);
        } catch {
          patch.undo();
        }
      },
    }),
    /**
     * Remove one note: immediately, not on the dialog's Save. Optimistic, so
     * the row disappears on click; a failure puts it back.
     */
    deleteVisionNote: builder.mutation<void, { projectId: number; noteId: number }>({
      query: ({ projectId, noteId }) => ({
        url: visionNotes.one(projectId, noteId),
        method: HttpMethod.DELETE,
      }),
      onQueryStarted: async ({ projectId, noteId }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          projectApi.util.updateQueryData("getVisionNotes", { projectId }, (draft) => {
            draft.notes = draft.notes.filter((note) => note.id !== noteId);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useCreateProjectMutation,
  useGetProjectsQuery,
  useLazyGetProjectsQuery,
  useGetProjectQuery,
  useDeleteProjectMutation,
  useCreateProjectLabelMutation,
  useGetProjectLabelsQuery,
  useGetProjectLabelUsageQuery,
  useGetArchivedProjectLabelsQuery,
  useArchiveProjectLabelMutation,
  useRestoreProjectLabelMutation,
  useUpdateProjectLabelMutation,
  useUpdateProjectMutation,
  useGetPreAnnotateSettingsQuery,
  useUpdatePreAnnotateSettingsMutation,
  useSuggestLabelDescriptionMutation,
  useGetVisionNotesQuery,
  useCreateVisionNoteMutation,
  useUpdateVisionNoteMutation,
  useDeleteVisionNoteMutation,
} = projectApi;
